// === BEGIN client/src/pages/Login.js (replace whole file) ===
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../assets/chitconnect-logo-blue.png';
import { apiFetch, refreshSocketAuth, refreshSessionUser, setToken } from '../lib/api';
import { registerPush } from '../lib/push';

// If you kept styles in a separate file, ensure this exists.
// Or remove this import if you pasted the CSS into App.css instead.
import './Login.css';

export default function Login({ setUser }) {
  const [form, setForm] = useState({ handle: '', password: '' }); // "handle" = email | mobile | username
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const navigate = useNavigate();

  // Prefill email/mobile when coming from an invite deep-link
  useEffect(() => {
    try {
      const h = sessionStorage.getItem('prefill_handle');
      if (h) {
        setForm((prev) => ({ ...prev, handle: h }));
        sessionStorage.removeItem('prefill_handle');
      }
    } catch {}
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    setLoading(true);

    const raw = (form.handle || '').trim();
    const normalizedPhone = raw.replace(/[^\d+]/g, '');
    const isEmail = /\S+@\S+\.\S+/.test(raw);
    const isMobile = /^(\+?\d{8,15})$/.test(normalizedPhone);

    const body = {
      email:    isEmail ? raw : '',
      mobile:   isMobile ? normalizedPhone : '',
      username: (!isEmail && !isMobile) ? raw : '',
      password: form.password,
    };

    try {
      const res = await apiFetch('/api/login', {
        method: 'POST',
        credentials: 'include', // ensure cookie cross-site
        body,
      });

      if (res.ok) {
        const data = await res.json().catch(() => ({}));

        if (data?.token) {
          setToken(data.token);
          // Refresh Socket.IO auth now that token exists
          refreshSocketAuth();
        } else {
          // If server didn't return a JWT, treat as failure for Fix C
          setError('Login failed: no token returned');
          setLoading(false);
          return;
        }

        const me = await refreshSessionUser();
        const user = me && me.user ? me.user : me;

        if (user) {
          sessionStorage.setItem('user', JSON.stringify(user));
          setUser?.(user);
        }

        // Attempt web push registration (best-effort)
        registerPush().catch(() => {});

        // Invite deep-link handoff
        const pendingInvite = sessionStorage.getItem('pending_invite');
        if (pendingInvite) {
          sessionStorage.removeItem('pending_invite');
          navigate(`/invite/${pendingInvite}`);
        } else {
          navigate('/');
        }
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data?.error || `Login failed (HTTP ${res.status})`);
      }
    } catch (err) {
      setError(err?.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <form className="login-form" onSubmit={handleSubmit} noValidate>
        <img className="login-logo" src={logo} alt="ChitConnect" />

        <h1 className="login-title">Welcome back</h1>
        <p className="login-subtitle">Sign in to continue</p>

        <div className="form-field">
          <label htmlFor="handle">Email or mobile</label>
          <input
            id="handle"
            name="handle"
            className={`input${error ? ' error' : ''}`}
            type="text"
            value={form.handle}
            onChange={handleChange}
            placeholder="e.g. alice@site.com or +61412345678"
            autoComplete="username"
            required
            inputMode="email"
          />
        </div>

        <div className="form-field password-field">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            name="password"
            className={`input${error ? ' error' : ''}`}
            type={showPw ? 'text' : 'password'}
            value={form.password}
            onChange={handleChange}
            autoComplete="current-password"
            required
          />
          <button
            type="button"
            className="password-toggle"
            onClick={() => setShowPw(s => !s)}
            aria-pressed={showPw}
            aria-label={showPw ? 'Hide password' : 'Show password'}
          >
            {showPw ? 'Hide' : 'Show'}
          </button>
        </div>

        {error && <div className="login-error" role="alert">{error}</div>}

        <button className="button-primary" type="submit" disabled={loading}>
          {loading ? 'Signing in…' : 'Sign in'}
        </button>

        <p style={{ marginTop: 14, textAlign: 'center', color: 'var(--cc-muted)' }}>
          Don’t have an account? <Link to="/register">Register</Link>
        </p>
      </form>
    </div>
  );
}
// === END client/src/pages/Login.js ===
